import { useSetAtom } from 'jotai';

import { Button, DrawerFooter } from '@growtherapy/sprout-ui';

import {
  ClientInformationDrawerTab as SeedlingClientInformationDrawerTab,
  useSetActiveClientInfoDrawerTab,
} from '@grow-therapy-team/seedling-components';

import { logger } from '../../../datadog';
import { useTrackEvent } from '../../../segment/segment';
import { EventTypeEnum } from '../../../segment/types';
import { activeClientInformationTabAtom } from '../../client-information/state';
import { AppointmentLocation } from '../../schedule-preview/types';
import { ClientInformationDrawerTab, ProviderDrawerState, drawerStateAtom } from '../../state';
import {
  AppointmentFollowUpSource,
  useCreateFollowUpAppointmentMutation,
} from '../../useCreateFollowUpAppointmentMutation';
import { EditAppointmentFormValues } from '../EditAppointmentDrawer';
import { ADD_APPOINTMENT_BUTTON_TITLE } from '../constants';
import { showAppointmentCreatedToast, showErrorToast } from '../toasts';

export function AddAppointmentDrawerFooter({
  isSaveDisabled,
  onAddAppointment,
  isLoading,
}: {
  isSaveDisabled: boolean;
  onAddAppointment: () => void;
  isLoading?: boolean;
}) {
  return (
    <DrawerFooter>
      <div className='flex w-full justify-end'>
        <Button disabled={isSaveDisabled} onClick={onAddAppointment} loading={isLoading}>
          {ADD_APPOINTMENT_BUTTON_TITLE}
        </Button>
      </div>
    </DrawerFooter>
  );
}

export function AddAppointmentDrawerFooterWrapper({
  isSaveDisabled,
  formValues,
  isLoading,
  providerId,
  patientCustomerId,
  patientShortId,
}: {
  isSaveDisabled: boolean;
  formValues: EditAppointmentFormValues;
  isLoading?: boolean;
  providerId: string;
  patientCustomerId: string;
  patientShortId: string;
}) {
  const setDrawerState = useSetAtom(drawerStateAtom);
  // NOTE: This atom is only consumed by the old drawer body. We can stop
  // double-writing once the `shouldUseSeedlingClientInfoDrawer` FF is fully
  // released and the old code path is removed.
  const setActiveTab = useSetAtom(activeClientInformationTabAtom);
  const setSeedlingActiveTab = useSetActiveClientInfoDrawerTab();

  const [addAppointment, { loading: isAppointmentLoading }] = useCreateFollowUpAppointmentMutation();
  const { formSubmitted } = useTrackEvent();
  const onAddAppointment = async () => {
    try {
      const result = await addAppointment({
        variables: {
          values: {
            providerId,
            patientCustomerId,
            startDate: formValues.startDate,
            endDate: formValues.endDate,
            indefinite: formValues.indefinite,
            numRecurrences: formValues.isRecurring ? formValues.numRecurrences : undefined,
            weeksBetween: formValues.isRecurring ? formValues.weeksBetween : undefined,
            inOffice: formValues.appointmentLocation ?? AppointmentLocation.Virtual,
            followUpSource: AppointmentFollowUpSource.Telehealth,
          },
        },
      });

      if (result?.data?.createFollowUpAppointment?.appointment?.id) {
        setDrawerState(ProviderDrawerState.CLIENT_INFORMATION);
        setActiveTab(ClientInformationDrawerTab.APPOINTMENTS);
        setSeedlingActiveTab(SeedlingClientInformationDrawerTab.APPOINTMENTS);
        showAppointmentCreatedToast();
        formSubmitted(EventTypeEnum.WAITING_ROOM_APPOINTMENT_CREATED, {
          appointmentShortId: result?.data?.createFollowUpAppointment?.appointment?.appointmentShortId,
          patientShortId: patientShortId,
        });
      }
    } catch {
      showErrorToast();
      logger.error('Failed to create new appointment');
    }
  };

  return (
    <AddAppointmentDrawerFooter
      isSaveDisabled={isSaveDisabled}
      onAddAppointment={onAddAppointment}
      isLoading={isAppointmentLoading || isLoading}
    />
  );
}
